import AnimatedCounter from "@/components/base/AnimatedCounter";
import SectionHeading from "@/components/base/SectionHeading";
import { useInView } from "@/hooks/useReveal";
import { carbonComparison, impactMetrics } from "@/mocks/home";

export default function Impact() {
  const { ref, inView } = useInView<HTMLDivElement>(0.25);

  return (
    <section id="impact" className="bg-background-100 py-20 md:py-28">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-10">
        <SectionHeading
          eyebrow="Carbon"
          title="From over 2 tonnes of CO₂ to near zero"
          description="Steel made with coal releases more than two tonnes of CO₂ for every tonne of steel. Hydrogen removes the oxygen from iron ore as water instead, so the reduction step itself releases almost nothing."
        />

        <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-12">
          <div className="grid grid-cols-2 gap-5 lg:col-span-7">
            {impactMetrics.map((metric, index) => (
              <div
                key={metric.label}
                className="reveal rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
                style={{ transitionDelay: `${index * 70}ms` }}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${metric.icon} text-xl`} />
                </span>
                <div className="mt-4 font-heading text-4xl font-semibold text-foreground-950 md:text-5xl">
                  <AnimatedCounter value={metric.value} suffix={metric.suffix} />
                </div>
                <div className="mt-2 font-heading text-sm font-semibold text-foreground-900">{metric.label}</div>
                <p className="mt-1 text-xs leading-relaxed text-foreground-500">{metric.detail}</p>
              </div>
            ))}
          </div>

          <div
            ref={ref}
            className="reveal rounded-lg border border-background-200 bg-background-50 p-6 md:p-8 lg:col-span-5"
          >
            <h3 className="font-heading text-lg font-semibold text-foreground-950">
              Tonnes of CO₂ per tonne of steel
            </h3>
            <p className="mt-1 text-sm text-foreground-500">Cradle-to-gate, by production route</p>

            <div className="mt-7 space-y-6">
              {carbonComparison.map((row, index) => (
                <div key={row.label}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground-800">{row.label}</span>
                    <span className="font-heading font-semibold text-foreground-950">{row.value} t</span>
                  </div>
                  <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-background-200">
                    <div
                      className={`h-full rounded-full ${row.tone} transition-all duration-[1400ms] ease-out`}
                      style={{
                        width: inView ? row.width : "0%",
                        transitionDelay: `${index * 180}ms`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-start gap-3 rounded-md bg-primary-50 p-4">
              <i className="ri-information-line mt-0.5 text-primary-700" />
              <p className="text-xs leading-relaxed text-foreground-700">
                Under the Ministry of Steel notification of 12 December 2024, finished steel below 2.2 t CO₂e per
                tonne is green steel, rated up to five stars below 1.6 t.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}